import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import jsSHA from "jssha";
import { v4 as uuidv4 } from "uuid";
import Loader from "../../../../components/Loader";
import { useCommon } from "../../../../context/CommonContext";
import { ncdAPI } from "../../../../services/ncd.api";
import {
  formatToInputDate,
  formatToApiDate,
  calculateAge
} from "../../../../helpers/common-helper";

import {
  Box,
  Typography,
  Stack,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import "./Addmember.css";

import { initialValues } from "./initialValues";
import { validationSchema } from "./validationSchema";
import { useLocationData } from "./../../hooks/useLocationData";
import { useFormHandler } from "./../../hooks/useFormHandler";
import { useFacilityDetails } from "./../../hooks/useFacilityDetails";
import { convertArrayFieldsToString, formatIndividualIdsPayload } from "./../../utils/formUtils"

import SubcenterSection from "./SubcenterSection";
import FamilyInformation from "./FamilyInformation";
import IndividualInformation from "./IndividualInformation";
import FormFooter from "./FormFooter";
import CommonConfirmDialog from "./CommonConfirmDialog";

const MEDICAL_ROLES = ["MO", "DEO", "STAFF_NURSE"];
const MIN_AGE_THRESHOLD = 30;
const MAX_IMAGE_SIZE = 100 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

const ERROR_KEY_MAP = {
  "familyInfo.addressInfo.villageId": "villageId",
  "familyInfo.addressInfo.villageOther": "villageOther",
  "individualInfo.name": "name",
  "individualInfo.surname": "surname",
  "individualInfo.gender": "gender",
  "individualInfo.age": "age",
  "individualInfo.birthDate": "birthDate",
  "individualInfo.mobileNumber": "mobileNumber",
  "individualInfo.pvtgIndividual": "pvtgIndividual",
  "individualInfo.healthInsuranceDetails": "healthInsuranceDetails"
};

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const encryptValue = (inputValue) => {
  const hashValue = new jsSHA("SHA-256", "TEXT", { numRounds: 1 });
  hashValue.update(inputValue);
  return hashValue.getHash("HEX");
};

const Addmember = ({
  hideSection,
  profileData,
  setshowCBACform,
  familyInfo,
  setShowNcdReg
}) => {
  const { ncduser } = useCommon();

  const {
    districts,
    talukas,
    villages,
    fetchDistricts,
    fetchTalukas,
    setVillages,
  } = useLocationData();

  const [formData, setFormData] = useState(initialValues);

  const { handleNestedInputChange } = useFormHandler(setFormData);

  const { formDetails, subusers, ashas, phcs, subcenters, getDetails } = useFacilityDetails({
    familyInfo,
    fetchDistricts,
    fetchTalukas,
    setVillages,
  });

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [enrollmentForId, setEnrollFId] = useState("");
  const [user, setUser] = useState(null);
  const [selectedTaluka, setSelectedTaluka] = useState("");
  const [selectedPHC, setSelectedPHC] = useState(null);
  const [selectedSubcenter, setSelectedSubcenter] = useState(null);
  const [age, setAge] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [photoError, setPhotoError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [enrollmentDate, setEnrollmentDate] = useState(getCurrentDate);

  const nameRef = useRef(null);
  const genderRef = useRef(null);
  const ageRef = useRef(null);
  const mobileNumberRef = useRef(null);
  const villageRef = useRef(null);
  const birthDateRef = useRef(null);
  const fileInputRef = useRef(null);

  const isMedicalRole = useMemo(() =>
    MEDICAL_ROLES.includes(user?.rolename),
    [user?.rolename]
  );

  useEffect(() => {
    if (!ncduser) return;

    const enrollId = encryptValue(ncduser.loginuser + new Date().getTime() + uuidv4());
    setEnrollFId(enrollId);

    setUser(ncduser);

    setIsPageLoading(true);
    getDetails(
      ncduser.facilityTypeId,
      ncduser.facility,
      ncduser.facilityType,
      ncduser.rolename
    ).finally(() => setIsPageLoading(false));

    setFormData((prevData) => ({
      ...prevData,
      individualInfo: {
        ...prevData.individualInfo,
        additionalDetails: {
          ...prevData.individualInfo.additionalDetails,
          enrollmentDate: formatToApiDate(enrollmentDate),
        },
      },
    }));

  }, [ncduser, enrollmentDate, getDetails]);

  useEffect(() => {
    if (!user) return;

    const addressInfo = familyInfo?.addressInfo || {};

    setFormData((prev) => ({
      ...prev,
      familyInfo: {
        ...prev.familyInfo,
        addressInfo: {
          ...prev.familyInfo.addressInfo,
          ...addressInfo,
          subcenterName: user.facilityName,
          subcenterId: user.facility
        },
        additionalDetails: {
          ...prev.familyInfo.additionalDetails,
          ...(familyInfo?.additionalDetails || {})
        }
      },
      individualInfo: {
        ...prev.individualInfo,
        additionalDetails: {
          ...prev.individualInfo.additionalDetails,
          anmName:
            prev.individualInfo.additionalDetails.anmName || user.username
        }
      }
    }));
  }, [user, familyInfo]);

  useEffect(() => {
    if (selectedPHC && formDetails) {
      setFormData((prev) => ({
        ...prev,
        familyInfo: {
          ...prev.familyInfo,
          addressInfo: {
            ...prev.familyInfo.addressInfo,
            phc: formDetails.phc || familyInfo?.addressInfo?.phc,
            phcId: formDetails.phcId || familyInfo?.addressInfo?.phcId
          },
        },
      }));
    }
  }, [selectedPHC, formDetails, familyInfo]);

  useEffect(() => {
    if (enrollmentForId) {
      setFormData((prev) => ({
        ...prev,
        enrollmentFormId: enrollmentForId,
      }));
    }
  }, [enrollmentForId]);

  const handleChange = useCallback((e) => {
    const { name, value, type } = e.target;

    let parsedValue = value;

    if (name === "individualInfo.pvtgIndividual") {
      const pvtgValue = value === "PVTG";
      setFormData((prev) => ({
        ...prev,
        individualInfo: {
          ...prev.individualInfo,
          pvtgIndividual: pvtgValue,
        },
      }));
      return;
    }

    if (type === "radio" && (value === "true" || value === "false")) {
      parsedValue = value === "true";
    }

    let calculatedAge = null;

    if (name === "individualInfo.birthDate") {
      if (value && value.trim() !== "") {
        try {
          const dateObj = new Date(value);
          calculatedAge = calculateAge(dateObj);
          if (!isNaN(calculatedAge) && calculatedAge >= 0) {
            setAge(calculatedAge);
          } else {
            setAge(null);
          }
        } catch (error) {
          setAge(null);
        }
      } else {
        setAge(null);
      }
    }

    const fieldKey = ERROR_KEY_MAP[name];

    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (fieldKey && newErrors[fieldKey]) {
        delete newErrors[fieldKey];
      }
      return newErrors;
    });

    setFormData((prevState) => {
      const keys = name.split(".");

      if (keys.length === 1) {
        return { ...prevState, [name]: parsedValue };
      }

      const updatedData = { ...prevState };
      let current = updatedData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = parsedValue;

      if (calculatedAge !== null) {
        updatedData.individualInfo.age = calculatedAge;
      }

      return updatedData;
    });
  }, []);

  const handleVillageChange = useCallback(async (e) => {
    const villageId = e.target.value;
    setIsPageLoading(true);

    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors["villageId"];
      return newErrors;
    });

    const selectedVillage = villages.find(
      (v) => (v.locationId || v.id || v.villageId) === villageId
    );

    const villageName = selectedVillage?.locationName || selectedVillage?.name || selectedVillage?.villageName;

    setFormData((prev) => ({
      ...prev,
      familyInfo: {
        ...prev.familyInfo,
        addressInfo: {
          ...prev.familyInfo.addressInfo,
          villageId: villageId,
          village: villageName,
          villageOther:
            villageName !== "Other"
              ? ""
              : prev.familyInfo.addressInfo.villageOther,
        },
      },
    }));

    setIsPageLoading(false);
  }, [villages, isMedicalRole, selectedSubcenter, user]);

  const handleSubcenterChange = useCallback(async (e) => {
    const subcenterId = e.target.value;
    setSelectedSubcenter(subcenterId);

    if (subcenterId && isMedicalRole) {
      setIsPageLoading(true);
      try {
        const response = await ncdAPI.getVillages(subcenterId);

        if (response?.data) {
          setVillages(response?.data?.villages || []);
        }
      } catch (error) {
        console.error("Error fetching subcenter details:", error);
      }
      setIsPageLoading(false);
    }
  }, [isMedicalRole, setVillages]);

  const handleTalukaChange = useCallback(async (e) => {
    const talukaId = String(e.target.value);
    setSelectedTaluka(talukaId);

    if (talukaId && isMedicalRole) {
      setIsPageLoading(true);
      try {
        setSelectedPHC(null);
        setSelectedSubcenter(null);
      } catch (error) {
        console.error("Error fetching taluka children:", error);
      }
      setIsPageLoading(false);
    }
  }, [isMedicalRole]);

  const handlePHCChange = useCallback(async (e) => {
    const phcId = e.target.value;
    setSelectedPHC(phcId);

    if (phcId && isMedicalRole) {
      setSelectedSubcenter(null);
    }
  }, [isMedicalRole]);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = "";
    setPhotoError("");

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setPhotoError("Supported formats: GIF, JPG, JPEG, PNG");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setPhotoError("Image size should not exceed 100 KB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.replace(/^data:image\/\w+;base64,/, "");

      setFormData((prev) => ({
        ...prev,
        individualInfo: {
          ...prev.individualInfo,
          photo: base64,
        },
      }));
    };

    reader.readAsDataURL(file);
  }, []);

  const handleRemoveImage = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      individualInfo: {
        ...prev.individualInfo,
        photo: "",
      },
    }));

    setPhotoError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleDateChange = useCallback((newValue) => {
    if (!newValue) return;

    const formattedDate = newValue.format("DD-MM-YYYY");

    setEnrollmentDate(formatToInputDate(formattedDate));

    setFormData((prevData) => ({
      ...prevData,
      individualInfo: {
        ...prevData.individualInfo,
        additionalDetails: {
          ...prevData.individualInfo.additionalDetails,
          enrollmentDate: formattedDate,
        },
      },
    }));
  }, []);

  const handleFormSubmit = useCallback(async (e, action, confirmed = false) => {
    if (e) e.preventDefault();

    try {
      await validationSchema.validate(formData, {
        abortEarly: false,
      });

      setFormErrors({});
    } catch (err) {
      const errors = {};

      err.inner.forEach((error) => {
        const key = ERROR_KEY_MAP[error.path] || error.path;
        errors[key] = error.message;

        toast.error(error.message, {
          className: "custom-toast-error",
        });
      });

      setFormErrors(errors);

      const firstErrorPath = err.inner[0]?.path;
      const element = document.querySelector(`[name="${firstErrorPath}"]`);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        element.focus();
      }

      return;
    }

    let birthDate = formData.individualInfo.birthDate;
    if (birthDate && /^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      const [yyyy, mm, dd] = birthDate.split("-");
      birthDate = `${dd}-${mm}-${yyyy}`;
    }

    if (!confirmed && formData.individualInfo.age !== null && formData.individualInfo.age < MIN_AGE_THRESHOLD) {
      setConfirmData({ action });
      setConfirmOpen(true);
      return;
    }

    const payload = {
      ...formData,
      familyInfo: {
        ...formData.familyInfo,
        additionalDetails: convertArrayFieldsToString(
          formData.familyInfo.additionalDetails
        ),
      },
      individualInfo: {
        ...formData.individualInfo,
        name: formData.individualInfo.name?.trim(),
        surname: formData.individualInfo.surname?.trim() || null,
        birthDate: birthDate,
        individualIds: formatIndividualIdsPayload(
          formData.individualInfo.individualIds
        ),
      },
    };

    try {
      setIsPageLoading(true);

      const response = await ncdAPI.addFamilyMember(payload, familyInfo?.familyId);

      if (response.data?.familyId) {
        
        toast.success(`Family member has been added Successfully`);

        setFormData(initialValues);
        setFormErrors({});
        setAge(null);
        setPhotoError("");
        setSelectedTaluka("");
        setSelectedPHC(null);
        setSelectedSubcenter(null);
        setEnrollmentDate(getCurrentDate());

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        setIsPageLoading(false);

        setTimeout(() => {
          if (action === "submitAndMove") {
            setshowCBACform(true);
            hideSection("FamilyForm");
            setShowNcdReg();
          } else {
            hideSection("FamilyForm");
            setShowNcdReg();
          }
        }, 1000);

      } else {
        toast.error("Error in Add member");
        setIsPageLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong", {
        className: "custom-toast-error",
      });
      setIsPageLoading(false);
    }
  }, [formData, familyInfo, hideSection, setshowCBACform, setShowNcdReg]);

  const handleConfirm = useCallback(() => {
    setConfirmOpen(false);
    if (confirmData) {
      handleFormSubmit(null, confirmData.action, true);
    }
  }, [confirmData, handleFormSubmit]);

  const handleCancel = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    hideSection("FamilyForm");
    setShowNcdReg();
  }, [hideSection, setShowNcdReg]);

  return (
    <Box
      sx={{
        p: 3,
        px: 0,
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        position: "relative",
      }}
    >

      {isPageLoading && <Loader />}

      <CommonConfirmDialog
        open={confirmOpen}
        message="This individual is under 30 years of age. Are you sure that you want to continue?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <Box
        component="form"
        sx={{
          backgroundColor: "#fff",
          p: 3,
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <img
            src={"/images/programs_logo/ncd.svg"}
            alt="NCD"
            style={{ width: "43px" }}
          />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Family Folder</Typography>
        </Box>
        <Button
          onClick={handleClose}
          sx={{
            border: "2px solid #A9A9A9",
            width: "30px",
            height: "30px",
            borderRadius: "100%",
            cursor: "pointer",
            position: "absolute",
            right: "-8px",
            top: "10px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "auto",
            p: 0,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.05)",
            },
          }}
        >
          <img src={"/images/closing.svg"} alt="Close" style={{ width: "16px" }} />
        </Button>

        <Box sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Enrollment Date"
              format="DD/MM/YYYY"
              value={enrollmentDate ? dayjs(enrollmentDate) : null}
              maxDate={dayjs()}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: "small",
                  error: Boolean(formErrors.enrollmentDate),
                  helperText: formErrors.enrollmentDate,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Stack spacing={3}>
          <SubcenterSection
            districts={districts}
            talukas={talukas}
            villages={villages}
            user={user}
            selectedDistrict={familyInfo?.addressInfo?.districtId || formDetails?.locationHierarchy?.districtId || formDetails?.districtId}
            selectedTaluka={familyInfo?.addressInfo?.talukaId || formDetails?.locationHierarchy?.talukaId || formDetails?.talukaId || selectedTaluka}
            selectedPHC={familyInfo?.addressInfo?.phcId || formDetails?.phcId || selectedPHC}
            selectedSubcenter={familyInfo?.addressInfo?.subcenterId || selectedSubcenter}
            selectedVillage={formData.familyInfo.addressInfo.villageId}
            formData={formData}
            handleChange={handleChange}
            handleVillageChange={handleVillageChange}
            handleSubcenterChange={handleSubcenterChange}
            handleTalukaChange={handleTalukaChange}
            handlePHCChange={handlePHCChange}
            phcs={phcs}
            subcenters={subcenters}
            subusers={subusers}
            ashas={ashas}
            formDetails={formDetails}
            formErrors={formErrors}
            villageRef={villageRef}
            familyInfo={familyInfo}
          />

          <FamilyInformation
            formik={null}
            formData={formData}
            handleChange={handleChange}
            formErrors={formErrors}
          />

          <IndividualInformation
            formik={null}
            formData={formData}
            handleChange={handleChange}
            handleNestedInputChange={handleNestedInputChange}
            formErrors={formErrors}
            photoError={photoError}
            setPhotoError={setPhotoError}
            handleImageChange={handleImageChange}
            handleRemoveImage={handleRemoveImage}
            age={age}
            nameRef={nameRef}
            birthDateRef={birthDateRef}
            genderRef={genderRef}
            ageRef={ageRef}
            mobileNumberRef={mobileNumberRef}
          />

          <FormFooter
            formik={null}
            handleFormSubmit={handleFormSubmit}
            isPageLoading={isPageLoading}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default Addmember;
